import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { errorNotification } from '../../components/notifications';
import api from '../../libs/api';

const initialState = {
  financialSummary: {
    date: Date.now(),
    banks: [],
    fixedAssets: [],
    assetAdjustments: [],
    directExpenses: [],
    directIncome: [],
    purchaseDirectExpenses: [],
    salesDirectIncome: [],
    inDirectExpenses: [],
    otherIncome: [],
    indirectIncome: [],
    sales: 0,
    purchases: 0,
    purchaseCreditNotes: 0,
    purchaseDebitNotes: 0,
    saleCreditNotes: 0,
    saleDebitNotes: 0,
    totalPurchases: 0,
    totalSales: 0,
    openingMaterialInventory: 0,
    closingMaterialInventory: 0,
    openingProductInventory: 0,
    closingProductInventory: 0,
    openingFlowMaterialInventory: 0,
    closingFlowMaterialInventory: 0,
    totalOpeningInventory: 0,
    totalClosingInventory: 0,
    tvc: 0,
    totalDirectExpenses: 0,
    totalDirectIncome: 0,
    totalIndirectExpenses: 0,
    totalOtherIncome: 0,
    totalIndirectIncome: 0,
    grossProfit: 0,
    grossProfitPercentage: 0,
    totalDepriciation: 0,
    netProfitMargin: 0,
    netProfitMarginPercentage: 0,
    bills: 0,
    manufacturingExpenses: [],
    totalManufacturingExpenses: 0,
  },
  balanceSheet: {
    date: Date.now(),
    initialCapitalAccount: 0,
    closingCapitalAccount: 0,
    initialCapitalAccounts: [],
    closingCapitalAccounts: [],
    initialLoan: 0,
    initialLoans: [],
    closingLoan: 0,
    closingLoans: [],
    initialOtherLoan: 0,
    initialOtherLoans: [],
    closingOtherLoan: 0,
    closingOtherLoans: [],
    initialCurrentLiability: 0,
    initialCurrentLiabilities: [],
    closingCurrentLiability: 0,
    closingCurrentLiabilities: [],
    initialAssetValue: 0,
    initialAssets: [],
    closingAssetValue: 0,
    closingAssets: [],
    initialBankBalances: [],
    closingBankBalances: [],
    openingPartyReceivable: [],
    totalOpeningPartyReceivable: 0,
    openingPartyPayable: [],
    totalOpeningPartyPayable: 0,
    closingPartyReceivable: [],
    totalClosingPartyReceivable: 0,
    closingPartyPayable: [],
    totalClosingPartyPayable: 0,
    openingMaterialInventory: 0,
    closingMaterialInventory: 0,
    openingProductInventory: 0,
    closingProductInventory: 0,
    openingFlowMaterialInventory: 0,
    closingFlowMaterialInventory: 0,
    totalOpeningInventory: 0,
    totalClosingInventory: 0,
    initialCurrentAssets: [],
    initialCurrentAsset: 0,
    closingCurrentAssets: [],
    closingCurrentAsset: 0,
    initialBankBalancesTotal: 0,
    closingBankBalancesTotal: 0,
    initialLiabilitiesSideTotal: 0,
    initialAssetsSideTotal: 0,
    closingLiabilitiesSideTotal: 0,
    closingAssetsSideTotal: 0,
    initialBankCCLimitBalances: [],
    initialBankCCLimitBalancesTotal: 0,
    closingBankCCLimitBalances: [],
    closingBankCCLimitBalancesTotal: 0,
    sales: 0,
    purchases: 0,
    bills: 0,
    purchaseCreditNotes: 0,
    purchaseDebitNotes: 0,
    saleCreditNotes: 0,
    saleDebitNotes: 0,
    totalPurchases: 0,
    totalSales: 0,
    otherIncome: [],
    totalOtherIncome: 0,
    directExpenses: [],
    totalDirectExpenses: 0,
    directIncome: [],
    totalDirectIncome: 0,
    manufacturingExpenses: [],
    totalManufacturingExpenses: 0,
    tvc: 0,
    grossProfit: 0,
    netProfit: 0,
    balanceSheetBalance: 0,
    inDirectExpenses: [],
    totalIndirectExpenses: 0,
    indirectIncome: [],
    totalIndirectIncome: 0,
    totalDepriciation: 0,
    assetAdjustments: [],
    workingCapital: 0,
    quickRatio: 0,
    workingCapitalRatio: 0,
    currentRatio: 0,
    debtorTurnOverDays: 0,
    inventoryTurnoverDays: 0,
    creditorTurnoverDays: 0,
    cashConversionDays: 0,
    cashConversionAmount: 0,
  },
  subgroupTransactions: [],
  accountsReport: {
    date: Date.now(),
    data: [],
  },
};

export const getFinancialSummary = createAsyncThunk(
  'financials/getFinancialSummary',
  async ({ from, to, consignorIds }) => {
    const date = Date.now();

    const { data } = await api.get('/financials/summary', {
      from,
      to,
      consignorIds,
    });

    return { ...data, date };
  },
);

export const getBalanceSheet = createAsyncThunk(
  'financials/getBalanceSheet',
  async ({ from, to, consignorIds }) => {
    const date = Date.now();

    const { data } = await api.get('/financials/balance-sheet', {
      from,
      to,
      consignorIds,
    });

    return { ...data, date };
  },
);

export const getAccountsReport = createAsyncThunk(
  'financials/getAccountsReport',
  async ({ from, to, consignorIds }) => {
    const date = Date.now();

    const { data } = await api.get('/financials/accounts', {
      from,
      to,
      consignorIds,
    });

    return { data, date };
  },
);

export const getSubgroupTransactions = createAsyncThunk(
  'financials/getSubgroupTransactions',
  async ({ from, to, consignorIds, subgroup, isAsset, group }) => {
    const { data } = await api.get('/accounts/transactions/details', {
      from,
      to,
      consignorIds: [consignorIds],
      subgroup,
      isAsset,
      group,
    });

    return data;
  },
);

const financials = createSlice({
  name: 'financials',

  initialState,

  reducers: {
    clearFinancialSummary: draft => {
      draft.financialSummary = {
        ...initialState.financialSummary,
        date: Date.now(),
      };
    },

    clearBalanceSheet: draft => {
      draft.balanceSheet = {
        ...initialState.balanceSheet,
        date: Date.now(),
      };
    },

    clearAccountsReport: draft => {
      draft.accountsReport = {
        ...initialState.accountsReport,
        date: Date.now(),
      };
    },

    clearSubgroupTransactions: draft => {
      draft.subgroupTransactions = [];
    },
  },

  extraReducers: builder => {
    builder.addCase(getFinancialSummary.fulfilled, (draft, { payload }) => {
      if (draft.financialSummary.date <= payload.date) {
        draft.financialSummary = payload;
      }
    });

    builder.addCase(getBalanceSheet.fulfilled, (draft, { payload }) => {
      if (draft.balanceSheet.date <= payload.date) {
        draft.balanceSheet = payload;
      }
    });

    builder.addCase(getAccountsReport.fulfilled, (draft, { payload }) => {
      if (draft.accountsReport.date <= payload.date) {
        draft.accountsReport = payload;
      }
    });

    builder.addCase(getSubgroupTransactions.fulfilled, (draft, { payload }) => {
      draft.subgroupTransactions = payload;
    });

    builder.addMatcher(
      isAnyOf(
        getFinancialSummary.rejected,
        getBalanceSheet.rejected,
        getAccountsReport.rejected,
        getSubgroupTransactions.rejected,
      ),
      (_Draft, { error }) => {
        errorNotification(error);
      },
    );
  },
});

export const {
  clearFinancialSummary,
  clearBalanceSheet,
  clearAccountsReport,
  clearSubgroupTransactions,
} = financials.actions;

export default financials.reducer;
